import { useContext, useEffect, useState, useCallback } from 'react';
import _ from 'lodash';
import {
  setTrk,
  TrkTag,
  TrkElement,
} from '@nerdwallet/nw-client-lib/trk-helper';

import { ImpressionContext } from '@nerdwallet/analytics/react/impression';
import useAnalytics from '../../lib/segment/useAnalytics';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from '@nerdwallet/currency/components/Button';
import { Link } from '@nerdwallet/currency/components/Link';
import { Text } from '@nerdwallet/currency/components/Text';
import { REGISTRATION_MODALS } from '@nerdwallet/js-auth-tools/dist/registration';

import { getLogger } from '@nerdwallet/logging';
import { transformTaxonomy } from '../../lib/helpers';
import type { ReduxStore } from '../redux/types';
import useContextualIgnorableModal from '../hooks/useContextualIgnorableModal';
import { useStoredExperiment } from '../hooks/useStoredExperiment';
import { EXPERIMENTS } from '../lib/experiments';
import { SET_GLOBAL_NAV_STATE } from '../redux/actions';
import { getCookie } from '../../../global-nav/components/utilities/cookies';

const rootLogger = getLogger('TrackedButton');

const NW_HOME_TAXES = '/home/taxes';

const buttonTypes = ['signin', 'register'] as const;

const links = {
  signin: '/home/signin',
  register: '/home/register',
};

const trkCopies = {
  signin: 'log in',
  register: 'join',
};

/* Returns information for tracking parameter trk_copy. */
const getTrkCopy = (type: (typeof buttonTypes)[number]) => {
  return trkCopies[type];
};

/* Adds tracking parameters. */
const getTrkParams = ({ copy = '', trk = {} }) => ({
  [TrkTag.element]: TrkElement.button,
  [TrkTag.copy]: copy.toLowerCase().split(' ').join('_'),
  ...trk,
});

/* Returns a link with tracking information.  */
const createTrkLink = ({
  trkParams,
  url,
  pageTaxonomy,
}: {
  trkParams: { [s: string]: string | number };
  url: string;
  pageTaxonomy: ReduxStore['pageTaxonomy'];
}) => {
  return setTrk({
    url,
    params: trkParams,
    taxonomy: transformTaxonomy(pageTaxonomy),
  });
};

/* Returns a valid button type. */
const validateButtonType = (type: (typeof buttonTypes)[number]) => {
  const logger = getLogger('validateButtonType', rootLogger);
  if (buttonTypes.includes(type)) {
    return type;
  }
  logger.error('Error finding valid button type. Defaulting to register type.');
  return 'register';
};

interface Props {
  buttonName?: string;
  buttonType?: (typeof buttonTypes)[number];
  handleClick?: ({ impressionId }: { impressionId: string }) => void;
  isPrimary?: boolean;
  isTertiary?: boolean;
  styleClasses?: string;
  isLink?: boolean;
  trkLocation?: string;
  trkFormat?: string;
  cohort?: string;
}

const TrackedButton: React.FC<Props> = ({
  buttonName = 'Register',
  buttonType = 'register',
  handleClick,
  isPrimary = false,
  isTertiary = false,
  styleClasses,
  isLink,
  trkLocation,
  trkFormat,
  cohort,
}) => {
  const pageTaxonomy = useSelector<ReduxStore, ReduxStore['pageTaxonomy']>(
    (store) => store.pageTaxonomy
  );

  // bain-1580: redirect to home taxes experiment
  const { regFlow } = useContextualIgnorableModal() ?? {};
  const isRedirectToHomeTaxesActive =
    regFlow ===
    REGISTRATION_MODALS.REDIRECT_TO_MY_TAXES_CONTEXTUAL_IGNORABLE_MODAL;

  const segmentTrackingAttributes = useSelector<
    ReduxStore,
    ReduxStore['segmentTrackingAttributes']
  >((store) => store.segmentTrackingAttributes);

  const [btnType, setBtnType] = useState(validateButtonType(buttonType));
  // TODO-TS: fix this once ImpressionContext is typed
  const impressionContext = useContext(ImpressionContext) as any;
  const impressionId = impressionContext?.impressionId; // impressionContext may not be defined in test environment

  /* Sets btnType state and revalidates if buttonType prop changes. */
  useEffect(() => {
    const type = validateButtonType(buttonType);
    setBtnType(type);
  }, [buttonType]);

  /* Creates a tracked link for Button using a valid button type. */
  const trkCopy = getTrkCopy(btnType);

  // to-do: clean up experiment code to make Auth0 the default experience after full rollout
  const auth0Experiment = useStoredExperiment(EXPERIMENTS.auth0.id);

  const isAuth0BetaEnabled =
    auth0Experiment?.assignedVariantName ===
    EXPERIMENTS.auth0.variants.VARIANT.id;

  const enrichedSegmentTrackingAttributes = {
    driverPageName: segmentTrackingAttributes?.driverPageName,
    driverLocation: trkLocation,
    driverSubLocation: cohort,
    driverElement: 'button',
    deployableName: segmentTrackingAttributes?.deployableName,
    deployableVersion: segmentTrackingAttributes?.deployableVersion,
    driverPageUrl: window?.location?.href,
    driverPageViewId: segmentTrackingAttributes?.driverPageViewId,
    driverContentId: segmentTrackingAttributes?.driverContentId,
    driverContentSource: segmentTrackingAttributes?.driverContentSource,
    driverFlowName: segmentTrackingAttributes?.driverFlowName,
    anonymousUserId: segmentTrackingAttributes?.anonymousUserId,
    mostRecentTraditionalDeviceId: getCookie('cookie_id'),
  };

  // Get anonymous ID from segment to pass to Auth0.
  // Getting anon ID from segment is async, so this must happen in a useEffect.
  const { getAnonymousId } = useAnalytics();

  const setHeaderPropsDispatch = useDispatch();

  const setHeaderProps = useCallback(
    (props) => {
      setHeaderPropsDispatch({
        ...props,
        type: SET_GLOBAL_NAV_STATE,
      });
    },
    [setHeaderPropsDispatch]
  );

  useEffect(() => {
    async function setAnonymousId() {
      const anonymousUserId = await getAnonymousId();
      if (
        anonymousUserId !== enrichedSegmentTrackingAttributes.anonymousUserId
      ) {
        setHeaderProps({
          segmentTrackingAttributes: { anonymousUserId },
        });
      }
    }
    setAnonymousId();
  }, [enrichedSegmentTrackingAttributes]);

  const trkParams = getTrkParams({
    copy: trkCopy,
    trk: isAuth0BetaEnabled
      ? enrichedSegmentTrackingAttributes
      : {
          origin_impression_id: impressionId,
          trk_location: trkLocation || 'global_nav',
          trk_format: trkFormat,
        },
  });

  const auth0Links = {
    signin: `/api/nts/v1/login?redirectTo=${window?.location?.protocol}//${window?.location?.host}/home/dashboard`,
    register: `/api/nts/v1/login?initialScreen=signup&redirectTo=${window?.location?.protocol}//${window?.location?.host}/home/dashboard`,
  };

  const link = createTrkLink({
    url: isAuth0BetaEnabled ? auth0Links[btnType] : links[btnType],
    trkParams,
    pageTaxonomy,
  });

  /* Opens a signin or register modal. */
  const handleAuthClick = (e) => {
    // if showAuthDialog is available use that - otherwise let the
    // user follow the link
    if (handleClick) {
      handleClick({ impressionId });
    }

    if (_.get(window, 'nwapi.ui.showAuthDialog') && !isAuth0BetaEnabled) {
      e.preventDefault();

      const params = new URLSearchParams(window?.location?.search);
      const redirectURI = params.get('redirect_uri')
        ? decodeURIComponent(params.get('redirect_uri'))
        : null;
      // If the current URL contains a redirect param within the /home path,
      // then redirect to that path after a successful login / registration.
      const successRedirect = redirectURI?.startsWith('/home')
        ? redirectURI
        : '/home/dashboard';

      (window as any).nwapi.ui.showAuthDialog({
        authType: btnType,
        trk: trkParams,
        flow: 'onboarding-generic',
        flowConfig: {
          googleButtonTheme: {
            theme: 'filled_blue',
            size: 'large',
          },
        },
        regTrackingAttributes: {
          cohort,
        },
        segmentTrackingAttributes: enrichedSegmentTrackingAttributes,
        version: 2,
        onSuccess: isRedirectToHomeTaxesActive
          ? NW_HOME_TAXES
          : successRedirect,
      });
    }
  };

  if (isLink) {
    return (
      <Link
        href={link}
        onClick={handleAuthClick}
        unstyled
        className={styleClasses}
      >
        <Text size="small" tone="light" bold>
          {buttonName}
        </Text>
      </Link>
    );
  }

  return (
    <Button
      href={link}
      onClick={handleAuthClick}
      primary={isPrimary}
      tertiary={isTertiary}
      className={styleClasses}
    >
      {buttonName}
    </Button>
  );
};

export default TrackedButton;
